import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import AssetService from '@/services/AssetService';
import type { Transaction } from '@/services/BankAccountService';
import TransactionFilterService, {
  type TransactionFilter,
} from '@/services/TransactionFilterService';
import { useQuery } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { createContext, useContext } from 'react';
import { toast } from 'sonner';

interface TransactionFiltersContextType {
  filters: TransactionFilter[] | undefined;
  getMatchingFilter: (
    transaction: Transaction,
  ) => TransactionFilter | undefined;
  refetchFilters: () => Promise<void>;
}

const TransactionFiltersContext =
  createContext<TransactionFiltersContextType | null>(null);

export function useTransactionFilters() {
  const context = useContext(TransactionFiltersContext);
  if (!context) {
    throw new Error(
      'useTransactionFilters must be used within TransactionFiltersProvider',
    );
  }
  return context;
}

export function TransactionFiltersProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: filters, refetch } = useQuery({
    queryKey: ['transaction-filters'],
    queryFn: () => TransactionFilterService.getFilters(),
  });

  const getMatchingFilter = (transaction: Transaction) => {
    if (!filters) return undefined;

    return filters.find((filter) => {
      // Check description pattern
      const matchesPattern = transaction.name
        .toLowerCase()
        .includes(filter.description_pattern.toLowerCase());

      // Check amount
      let matchesAmount = false;
      switch (filter.amount_condition) {
        case 'equals':
          matchesAmount = transaction.amount === filter.amount_value;
          break;
        case 'greater_than':
          matchesAmount = transaction.amount > filter.amount_value;
          break;
        case 'less_than':
          matchesAmount = transaction.amount < filter.amount_value;
          break;
      }

      // Check transaction type
      const matchesType =
        transaction.transaction_type === filter.transaction_type;

      return matchesPattern && matchesAmount && matchesType;
    });
  };

  return (
    <TransactionFiltersContext.Provider
      value={{ filters, getMatchingFilter, refetchFilters: refetch }}
    >
      {children}
    </TransactionFiltersContext.Provider>
  );
}

export function TransactionFilters() {
  const { filters, refetchFilters } = useTransactionFilters();
  const { data: assets } = useQuery({
    queryKey: ['assets'],
    queryFn: () => AssetService.getAssets(),
  });

  const handleDeleteFilter = async (filterId: number) => {
    try {
      await TransactionFilterService.deleteFilter(filterId);
      toast.success('Filter deleted successfully');
      refetchFilters();
    } catch (error) {
      console.error('Error deleting filter:', error);
      toast.error('Failed to delete filter');
    }
  };

  if (!filters?.length) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No filters found. Create a filter from a transaction to get started.
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Description Pattern</TableHead>
            <TableHead>Amount</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Splits</TableHead>
            <TableHead className="w-[100px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filters.map((filter) => (
            <TableRow key={filter.id}>
              <TableCell className="font-medium">{filter.name}</TableCell>
              <TableCell>{filter.description_pattern}</TableCell>
              <TableCell>
                <span
                  className={
                    filter.transaction_type === 'INCOME'
                      ? 'text-green-600'
                      : 'text-red-600'
                  }
                >
                  {filter.amount_condition === 'equals' && '='}
                  {filter.amount_condition === 'greater_than' && '>'}
                  {filter.amount_condition === 'less_than' && '<'}{' '}
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  }).format(filter.amount_value)}
                </span>
              </TableCell>
              <TableCell>{filter.transaction_type}</TableCell>
              <TableCell>
                <div className="space-y-1">
                  {filter.splits.map((split, index) => {
                    const asset = assets?.find((a) => a.id === split.assetId);
                    return (
                      <div key={index} className="text-sm">
                        {asset?.name}: {split.percentage}%
                      </div>
                    );
                  })}
                </div>
              </TableCell>
              <TableCell>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDeleteFilter(filter.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
